import { calendar, pilotDateMoves } from './content/calendar.ts';
import { byId } from './content/index.ts';
import { freshProgress } from './domain.ts';
import { validProgress } from './progress-validation.ts';
export { validProgress } from './progress-validation.ts';
import type { Progress } from './types.ts';
import { log } from './logger.ts';

export const STORAGE_KEY = 'daily.practice.v1';
type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;
/** Preserve original records and prefer any work already saved under the new date.
 * Completion days and history timestamps describe actual activity and are never shifted. */
export function migratePilotDates(progress: Progress): Progress {
  const attempts = { ...progress.attempts };
  const drafts = { ...progress.drafts };
  for (const { from: oldDay, to: newDay } of pilotDateMoves) {
    for (const exerciseId of Object.values(calendar[newDay])) {
      const exercise = byId[exerciseId];
      for (const question of [...exercise.questions, ...(exercise.retiredQuestions ?? [])]) {
        const oldKey = `daily:${oldDay}/${question.id}`;
        const newKey = `daily:${newDay}/${question.id}`;
        if (attempts[newKey] === undefined && progress.attempts[oldKey] !== undefined)
          attempts[newKey] = progress.attempts[oldKey];
        if (drafts[newKey] === undefined && progress.drafts[oldKey] !== undefined)
          drafts[newKey] = progress.drafts[oldKey];
      }
    }
  }
  // Only these two days moved after per-date Home choices were introduced.
  const dailySubjects = progress.dailySubjects && { ...progress.dailySubjects };
  if (dailySubjects) {
    for (const [from, to] of [
      ['2026-10-26', '2026-09-26'],
      ['2026-10-27', '2026-09-27'],
    ]) {
      if (dailySubjects[to] === undefined && progress.dailySubjects?.[from] !== undefined)
        dailySubjects[to] = [...progress.dailySubjects[from]];
    }
  }
  return { ...progress, attempts, drafts, ...(dailySubjects ? { dailySubjects } : {}) };
}
export interface LoadResult {
  progress: Progress;
  error: string | null;
  writable: boolean;
}
export function loadProgress(storage: StorageLike): LoadResult {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null) return { progress: freshProgress(), error: null, writable: true };
    const parsed: unknown = JSON.parse(raw);
    if (!validProgress(parsed)) throw new Error('Invalid saved progress');
    return { progress: migratePilotDates(parsed), error: null, writable: true };
  } catch {
    log('warn', 'storage.load_failed');
    return {
      progress: freshProgress(),
      error:
        'Your saved progress could not be loaded. You can keep practicing in this tab; the existing browser data will not be overwritten.',
      writable: false,
    };
  }
}
export function saveProgress(storage: StorageLike, progress: Progress): string | null {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(progress));
    return null;
  } catch {
    log('warn', 'storage.save_failed');
    return 'This browser could not save progress. You can keep practicing, but changes may be lost when this tab closes.';
  }
}
export function browserStorage(): StorageLike {
  // Access can itself throw when browser storage is disabled.
  return {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
  };
}
